import * as fs from "node:fs";
import * as os from "node:os";
import * as path from "node:path";
import { parse as parseYaml } from "yaml";
import { LocalCliError } from "./errors.js";

const A2A_PATH = "/api/a2a/mcp";
const BRAIN_MCP_PATH = "/api/brain-agent/mcp";

export interface GatewayUrlOptions {
  flag?: string;
  env?: NodeJS.ProcessEnv;
  home?: string;
}

/** Resolve the full A2A bridge URL (<gateway-base>/api/a2a/mcp).
 *  Precedence: --gateway-url flag → M8T_GATEWAY_URL env → ~/.m8t/config.yaml gatewayUrl. */
export function resolveBridgeUrl(opts: GatewayUrlOptions): string {
  return resolveGatewayEndpoint(opts, A2A_PATH, {
    code: "A2A_NO_GATEWAY_URL",
    purpose: "the A2A bridge",
  });
}

/** Resolve the narrow brain MCP endpoint using the same gateway precedence as A2A:
 *  --gateway-url flag -> M8T_GATEWAY_URL env -> ~/.m8t/config.yaml gatewayUrl. */
export function resolveBrainMcpUrl(opts: GatewayUrlOptions): string {
  return resolveGatewayEndpoint(opts, BRAIN_MCP_PATH, {
    code: "BRAIN_NO_GATEWAY_URL",
    purpose: "the brain MCP gateway",
  });
}

function resolveGatewayEndpoint(
  opts: GatewayUrlOptions,
  endpointPath: string,
  error: { code: string; purpose: string },
): string {
  const env = opts.env ?? process.env;
  const base =
    clean(opts.flag, endpointPath) ??
    clean(env.M8T_GATEWAY_URL, endpointPath) ??
    clean(readConfigGatewayUrl(opts.home), endpointPath);
  if (!base) {
    throw new LocalCliError({
      code: error.code,
      message: `Could not resolve the gateway URL for ${error.purpose}.`,
      hint: "Pass --gateway-url https://<gateway-fqdn>, set M8T_GATEWAY_URL, or add a gatewayUrl key to ~/.m8t/config.yaml.",
    });
  }
  let parsed: URL;
  try {
    parsed = new URL(base);
  } catch {
    throw new LocalCliError({
      code: error.code,
      message: `The gateway URL for ${error.purpose} is invalid.`,
      hint: "Use an absolute HTTPS URL such as https://gateway.example.com.",
    });
  }
  if (
    parsed.protocol !== "https:" || parsed.username || parsed.password ||
    parsed.search || parsed.hash || parsed.pathname !== "/"
  ) {
    throw new LocalCliError({
      code: error.code,
      message: `The gateway URL for ${error.purpose} must be a credential-free HTTPS URL.`,
      hint: "Use an absolute HTTPS URL such as https://gateway.example.com.",
    });
  }
  return `${base}${endpointPath}`;
}

function clean(v: string | undefined, endpointPath: string): string | undefined {
  if (!v || typeof v !== "string") return undefined;
  let t = v.trim().replace(/\/+$/, "");
  // Tolerate a full endpoint URL being passed (with or without a trailing slash)
  // so the canonical path is appended exactly once.
  if (t.toLowerCase().endsWith(endpointPath)) {
    t = t.slice(0, -endpointPath.length).replace(/\/+$/, "");
  }
  return t.length ? t : undefined;
}

function readConfigGatewayUrl(home?: string): string | undefined {
  const cfg = path.join(home ?? os.homedir(), ".m8t", "config.yaml");
  if (!fs.existsSync(cfg)) return undefined;
  try {
    const o = parseYaml(fs.readFileSync(cfg, "utf8")) as Record<string, unknown>;
    return typeof o.gatewayUrl === "string" ? o.gatewayUrl : undefined;
  } catch {
    return undefined;
  }
}
